import { useState } from 'react';
import {
  adminAccessRequests,
  adminInviteCreate,
  adminInvites,
  residentsRoster,
  adminUserResetLink,
  adminUserSessions,
  adminUsers,
  adminUserUpdate,
  approvalsDeputy,
  facilitySmsRoles,
  facilitySmsRolesSave,
  facilityStaff,
  facilityStaffRole,
  userAlertPhoneResend,
  TEAM_ROLES,
  type AdminInvite,
  type ApiError,
  type Role,
  type StaffCredential,
} from '@aithora/shared';
import { useConsoleRole, useSession } from '../../session/SessionContext';
import { Skeleton } from '../../ui/states';
import { BulkAdd } from './BulkAdd';
import { AlertPhoneEditor } from './AlertPhoneEditor';
import { NpiEditor } from './NpiEditor';
import { CredentialChip, CredentialEditor } from './CredentialEditor';
import { MedRulesPanel } from './MedRulesPanel';
import { AccessRequestRows, BuildingsList, InviteLink, OnboardWizard, PendingPeople, UserAccountActions } from './PeopleAccess';
import { AccountBlock, DeletionRequestsBlock, stateDot, stateStyle } from './AccountDeletion';
import { CustomRolePicker, CustomRolesPanel } from './RolesHouseholds';
import { ErrLine, I, btn, chip, fig, notServed, pill, pre, seg, sx, tileWrap, useApi, useGuarded, usePage , LoadError } from './kit';

type Pill = 'Super' | 'Admin' | 'Sales' | 'Dev' | 'Care' | 'Family' | 'Resident' | 'Nurse' | 'CNA';
interface UserRow {
  id: string;
  name: string;
  handle: string;
  email: string;
  role: string;
  /** The design's role chip (u[3]); care staff read "CARE". */
  chipRole: string;
  pill: Pill | null;
  status: string;
  multiSig: boolean;
  alertPhoneVerified?: boolean;
  alertPhonePending?: boolean;
  alertPhone?: string | null;
  npi?: string | null;
  /** Their medication credential (who may give which medicine), Facility Admin view only. */
  credential?: StaffCredential | null;
  /** The person's building, for its custom roles (null for an Aithora team account). */
  orgId?: string | null;
  customRoleId?: string | null;
  /** The owner account (founder): never suspended or deleted. */
  founder?: boolean;
}

const ROLE_TONE: Record<string, [string, string]> = {
  SUPER_ADMIN: ['#B3261E', 'rgba(179,38,30,.14)'], ADMIN: ['#264C7E', 'rgba(79,138,214,.16)'], SALES: ['#5B6B7C', 'rgba(91,107,124,.18)'],
  DEVELOPER: ['#44505E', 'rgba(68,80,94,.16)'], CARE: ['#12503C', 'rgba(47,138,111,.16)'],
};
const toneOf = (r: string) => ROLE_TONE[r === 'OPERATOR' ? 'ADMIN' : r] ?? ROLE_TONE.CARE;
const CHIP: Record<string, string> = { CAREGIVER: 'CARE', STAFF: 'CARE' };

/** The design's role word for a stored role (the pill a person's role lights). */
const PILL_OF: Record<string, Pill> = {
  SUPER_ADMIN: 'Super', OPERATOR: 'Admin', ADMIN: 'Admin', SALES: 'Sales', DEVELOPER: 'Dev', CAREGIVER: 'Care', STAFF: 'Care', FAMILY_MEMBER: 'Family', RESIDENT: 'Resident',
};
/** A pill back to the stored role it gives (team lens). */
const ROLE_OF_PILL: Record<string, string> = {
  Super: 'SUPER_ADMIN', Admin: 'OPERATOR', Sales: 'SALES', Dev: 'DEVELOPER', Care: 'CAREGIVER', Family: 'FAMILY_MEMBER', Resident: 'RESIDENT',
};

const PERMS = ['Full platform access', 'Manage all users & roles', 'Manage facilities', 'View resident data & reports',
  'CRM & sales pipeline', 'View outcomes & ROI reports', 'Log care & cognitive notes', 'Receive safety alerts', 'Billing & settings'];
/** What each role may do (GRANTS, L21899): the rule the server's role matrix holds. */
const GRANTS: Record<string, number[]> = {
  Super: [1, 1, 1, 1, 1, 1, 1, 1, 1], Admin: [0, 1, 1, 1, 0, 1, 0, 1, 1], Sales: [0, 0, 0, 0, 1, 1, 0, 0, 0],
  Dev: [0, 0, 0, 0, 0, 0, 0, 0, 0], Care: [0, 0, 0, 1, 0, 0, 1, 1, 0], Nurse: [0, 0, 0, 1, 0, 1, 1, 1, 0], CNA: [0, 0, 0, 1, 0, 0, 1, 1, 0],
  Family: [0, 0, 0, 0, 0, 0, 0, 1, 0], Resident: [0, 0, 0, 0, 0, 0, 0, 0, 0],
};

const SEG_OPT = (on: boolean) =>
  'display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:16px;text-decoration:none;white-space:nowrap;' +
  (on ? 'background:#2F5C97;color:#fff;font-weight:600;' : 'background:#F7F3EA;color:#3C454F;border:1px solid rgba(34,39,46,.16);');

const initials = (n: string) => n.split(' ').map((w) => w[0]).join('').slice(0, 2);
/** "Tomas Reyes" -> "Tomas R." (the design's alert-phone line). */
const firstInitial = (n: string) => {
  const w = n.split(',')[0].trim().split(/\s+/);
  return w.length < 2 ? w[0] : w[0] + ' ' + w[w.length - 1][0] + '.';
};

const DAY_MS = 86_400_000;
/** Where an invite stands, in words: the link is not used yet, waits for the owner, or ran out. */
export function inviteState(i: AdminInvite, now: number): { text: string; tone: 'wait' | 'open' | 'dead' } | null {
  if (i.status === 'accepted' || i.status === 'revoked') return null;
  const left = Date.parse(i.expires_at) - now;
  if (Number.isFinite(left) && left <= 0) return { text: 'Expired, not used', tone: 'dead' };
  if (i.status === 'awaiting_approval') return { text: 'Waiting for the owner’s approval', tone: 'wait' };
  const days = Number.isFinite(left) ? Math.max(1, Math.ceil(left / DAY_MS)) : null;
  return { text: 'Invited, not signed in yet' + (days ? ' · link works ' + days + (days === 1 ? ' more day' : ' more days') : ''), tone: 'open' };
}

/** Users & Permissions (design seIsUsers, T8779; users L21869-22442). */
export function UsersSection() {
  const { client, session } = useSession();
  const { lens, changed, facilityId } = usePage();
  const { canWrite, roReason, wr } = useGuarded();
  const fa = lens.lens === 'Facility Admin';
  const orgName = session?.user.orgName ?? 'your building';

  const [tab, setTab] = useState<'Aithora Team' | 'Facilities'>('Aithora Team');
  const [filter, setFilter] = useState('All');
  const [query, setQuery] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [invite, setInvite] = useState<{ name: string; email: string; role: string; title?: 'Nurse' | 'CNA'; residentId?: string } | null>(null);
  const [invited, setInvited] = useState(false);
  // The invite links made on this visit: nothing is emailed, so each one is shown to send.
  const [links, setLinks] = useState<Array<{ who: string; url: string; pending: boolean }>>([]);
  const [onboarded, setOnboarded] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [resetSent, setResetSent] = useState<string | null>(null);
  const [resetNote, setResetNote] = useState<string | null>(null);
  const [resent, setResent] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);

  const team = useApi((c, s) => adminUsers(c, { query: tab === 'Aithora Team' ? { scope: 'platform' } : {}, signal: s }), [tab], !fa);
  const staff = useApi((c, s) => facilityStaff(c, { params: { id: 'me' }, signal: s }), [], fa);
  const requests = useApi((c, s) => adminAccessRequests(c, { query: { status: 'pending' }, signal: s }), [], !fa);
  // Who approves (Andrew, 2026-10-01: no deputy for now). Aithora team view only.
  const deputy = useApi((c, s) => approvalsDeputy(c, { signal: s }), [], !fa);
  const smsRoles = useApi((c, s) => facilitySmsRoles(c, { params: { id: 'me' }, signal: s }), [], fa);
  // Invites made and not yet used (GET /admin/invites answers a Facility Admin, a Super
  // Admin and an Operator; a Partner is refused, so it is not asked).
  const { role: consoleRole } = useConsoleRole();
  const seesInvites = fa || consoleRole === 'SUPER_ADMIN' || consoleRole === 'OPERATOR';
  const invites = useApi((c, s) => adminInvites(c, { query: fa && facilityId ? { organizationId: facilityId } : {}, signal: s }), [facilityId], seesInvites);
  // A resident's own sign-in is for a resident already on the roster.
  const roster = useApi((c, s) => residentsRoster(c, { signal: s }), [], fa && invite?.role === 'RESIDENT');

  const users: UserRow[] = fa
    ? (staff.data?.staff ?? []).map((u) => ({
        // The handle is the sign-in name before the @, not the first word of the name
        // (which read "@Dr" for "Dr Greg House").
        id: u.id, name: u.name ?? u.email ?? 'Unnamed', handle: '@' + (u.email ? u.email.split('@')[0] : String(u.name ?? '').replace(/^(dr|mr|mrs|ms)\.?\s+/i, '').split(/\s/)[0]), email: u.email ?? '',
        role: u.role, chipRole: CHIP[u.role] ?? u.role, pill: u.role === 'ADMIN' ? 'Admin' : u.title === 'Nurse' ? 'Nurse' : 'CNA',
        status: u.status, multiSig: (u as { multiSig?: boolean }).multiSig === true, alertPhoneVerified: u.alertPhoneVerified, alertPhonePending: u.alertPhonePending === true, alertPhone: u.alertPhone, npi: u.npi ?? null, credential: u.credential ?? null,
        orgId: facilityId ?? session?.user.organizationId ?? null,
      }))
    : (team.data?.users ?? [])
        .filter((u) => (tab === 'Aithora Team' ? true : !TEAM_ROLES.has(u.role as Role)))
        .map((u) => ({
          id: u.id, name: u.name ?? u.email ?? 'Unnamed', handle: '@' + (u.username ?? String(u.name ?? '').split(' ')[0]), email: u.email ?? '',
          role: u.role, chipRole: CHIP[u.role] ?? u.role, pill: PILL_OF[u.role] ?? null, status: u.status, multiSig: u.multiSig,
          orgId: TEAM_ROLES.has(u.role as Role) ? null : u.organizationId, customRoleId: u.customRoleId, founder: u.founder === true,
        }));

  // The tab's invites: team roles on Aithora Team, building roles on Facilities.
  const nowMs = Date.now();
  const pendingInvites = (invites.data?.invites ?? [])
    .filter((i) => fa || (tab === 'Aithora Team' ? TEAM_ROLES.has(i.role as Role) : !TEAM_ROLES.has(i.role as Role)))
    .map((i) => ({ i, st: inviteState(i, nowMs) }))
    .filter((x): x is { i: AdminInvite; st: NonNullable<ReturnType<typeof inviteState>> } => x.st !== null);

  const loading = fa ? !staff.data && !staff.error : !team.data && !team.error;
  const loadErr = fa ? staff.error : team.error;

  const uQuery = query.trim().toLowerCase();
  let uRows = users.filter((u) => filter === 'All' || u.pill === filter || (filter === 'Admin' && u.role.indexOf('ADMIN') >= 0));
  if (uQuery) uRows = uRows.filter((u) => (u.name + ' ' + u.email).toLowerCase().indexOf(uQuery) >= 0);
  const sel = users.find((u) => u.id === selId) ?? users[fa ? 1 : 4] ?? users[0] ?? null;
  const sessions = useApi((c, s) => adminUserSessions(c, { params: { id: sel!.id }, signal: s }), [sel?.id], !!sel);
  const ROLES: Pill[] = fa ? ['Admin', 'Nurse', 'CNA'] : ['Super', 'Admin', 'Sales', 'Dev', 'Care', 'Family', 'Resident'];
  const selPill = sel?.pill ?? null;
  const grant = GRANTS[selPill ?? 'Dev'] ?? GRANTS.Dev;
  const PERM_SHOW = fa ? [3, 5, 6, 7] : [0, 1, 2, 3, 4, 5, 6, 7, 8];
  const lockR = !fa && !lens.superAdmin;

  const reloadUsers = () => (fa ? staff.reload() : team.reload());
  const act = async (fn: () => Promise<unknown>) => {
    setErr(null);
    try {
      await fn();
      changed();
    } catch (e) {
      setErr(e as ApiError);
    }
  };

  const setRole = (p: Pill) => {
    if (!sel || !canWrite) return;
    if (fa) {
      const role = p === 'Admin' ? 'ADMIN' : 'CAREGIVER';
      void act(async () => {
        await facilityStaffRole(client, { params: { id: 'me', userId: sel.id }, body: p === 'Admin' ? { role } : { role, title: p === 'Nurse' ? 'Nurse' : 'CNA' } });
        staff.reload();
      });
      return;
    }
    if (lockR) return;
    void act(async () => {
      await adminUserUpdate(client, { params: { id: sel.id }, body: { role: ROLE_OF_PILL[p] } });
      team.reload();
    });
  };

  const tiles = fa
    ? [
        ['Staff', String(users.length), orgName + ' only', '#22272E'],
        ['Admins', String(users.filter((u) => u.pill === 'Admin').length), 'can manage staff', '#2F5C97'],
        ['Nurses', String(users.filter((u) => u.pill === 'Nurse').length), 'licensed', '#2F5C97'],
        ['CNAs', String(users.filter((u) => u.pill === 'CNA').length), 'care staff', '#54606C'],
      ]
    : [
        ['Users', String(users.length), users.filter((u) => u.role === 'SUPER_ADMIN').length + ' super · ' + (users.length - users.filter((u) => u.role === 'SUPER_ADMIN').length) + ' others', '#22272E'],
        ['Operators', String(users.filter((u) => ['SUPER_ADMIN', 'OPERATOR', 'ADMIN'].includes(u.role)).length), 'can kill + approve', '#2F5C97'],
        ['Approvers', String(users.filter((u) => u.multiSig).length), 'multi-sig eligible', '#2F5C97'],
        ['Viewers', String(users.filter((u) => ['DEVELOPER', 'PARTNER'].includes(u.role)).length), 'read-only', '#54606C'],
        ['Active', String(users.filter((u) => u.status === 'active').length), 'accounts active', '#1F6B52'],
      ];

  // Alert phones (Facility Admin): how many are verified, and who has not confirmed.
  const unverified = users.filter((u) => u.alertPhone && !u.alertPhoneVerified);
  const withPhone = users.filter((u) => u.alertPhone);
  const firstUnv = unverified[0];
  // Resend reaches only a number with a code out for it (the server answers 409 otherwise).
  const resendable = unverified.find((u) => u.alertPhonePending) ?? null;
  // Active staff with no confirmed number cannot be paged by text or call: flagged on their row.
  const needsPhone = (u: UserRow) => fa && u.status !== 'deleted' && !u.alertPhoneVerified;
  const needN = users.filter(needsPhone).length;
  const phoneLine = (withPhone.length === 0
    ? 'no alert phones set yet'
    : withPhone.filter((u) => u.alertPhoneVerified).length + ' of ' + withPhone.length + ' verified' + (firstUnv ? (resent && resendable ? ' · code resent to ' + firstInitial(resendable.name) : ' · ' + firstInitial(firstUnv.name) + ' has not confirmed yet') : ''))
    + (needN ? ' · ' + needN + (needN === 1 ? ' needs a phone number' : ' need a phone number') : '');

  const pendingN = requests.data?.requests.length ?? 0;
  /** The Facilities tab (Aithora's lens), and whether any building's staff are on it yet. */
  const facTab = !fa && tab === 'Facilities';
  const facEmpty = facTab && users.length === 0;
  const faPending = users.filter((u) => u.status === 'pending_approval').length;
  const requestsNote = fa
    ? faPending ? faPending + (faPending === 1 ? ' person is' : ' people are') + ' waiting for Aithora to approve their account.' : 'No requests waiting. People you add wait here until Aithora approves their account.'
    : requests.error
      ? 'Access requests could not be loaded.'
      : pendingN
        ? pendingN + (pendingN === 1 ? ' request is' : ' requests are') + ' waiting. New requests from the public "Request access" form land here.'
        : 'No requests waiting. New requests from the public "Request access" form land here.';

  // The owner (by email) is the founder row; only they remove an Aithora team account.
  const viewerOwner = !fa && users.some((u) => u.founder && u.id === session?.user.id);
  const activeAdmins = users.filter((u) => u.role === 'ADMIN' && u.status === 'active').length;
  const nSessions = sessions.data?.sessions.length ?? 0;

  if (loading) return <Skeleton rows={6} label="Loading people" />;
  if (loadErr && !users.length) return <LoadError error={loadErr} onRetry={reloadUsers} />;

  return (
    <div data-testid="settings-users">
      {!fa && (
        <div role="tablist" aria-label="Whose accounts" style={sx('display:flex;align-items:center;gap:7px;flex-wrap:wrap;row-gap:6px;margin-bottom:13px;')}>
          {(['Aithora Team', 'Facilities'] as const).map((n) => (
            <a key={n} href="#" role="tab" aria-selected={tab === n} onClick={(e) => { pre(e); setTab(n); setSelId(null); }} style={sx(pill(tab === n))}>
              {I(n)}
            </a>
          ))}
        </div>
      )}
      {facTab && (
        // V9 (design v9SeFac, L9523): its own state on the Facilities tab, never the Aithora team repeated.
        <div data-testid="facility-teams" style={sx('display:flex;flex-direction:column;gap:6px;margin:6px 0 12px;padding:14px 16px;border-radius:10px;border:1px dashed rgba(34,39,46,.25);background:#FCFAF4;')}>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>Facility teams</span>
          <span style={sx('font-size:16px;color:#3C454F;line-height:1.5;max-width:90ch;')}>
            {users.length
              ? "Each building's staff accounts belong to that building and are managed by its Facility Admin. Aithora can see who is on each team but does not add care staff for them."
              : "Each building's staff accounts belong to that building and are managed by its Facility Admin. Aithora can see who is on each team but does not add care staff for them. First step: onboard a building and invite its Facility Admin; their team then appears here."}
          </span>
          {!users.length && (
            <div style={sx('display:flex;gap:8px;flex-wrap:wrap;margin-top:2px;')}>
              <a href="#" {...wr(() => { setOnboarded(false); setWizard(true); })} style={sx('display:inline-flex;align-items:center;min-height:40px;padding:0 14px;border-radius:8px;font-size:16px;font-weight:700;text-decoration:none;white-space:nowrap;background:#2F5C97;color:#fff;')}>
                Onboard facilities
              </a>
            </div>
          )}
        </div>
      )}
      {fa && (
        <>
          <div style={sx('font-size:16px;color:#54606C;margin-bottom:10px;')}>{`Your building's staff. Roles here only reach ${orgName}; Aithora's team is not shown.`}</div>
          <div style={sx('display:flex;align-items:center;gap:8px 16px;flex-wrap:wrap;padding:8px 0 10px;border-top:1px solid rgba(34,39,46,.14);margin-bottom:10px;')}>
            <span style={sx('flex:1 1 260px;min-width:0;')}>
              <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>
                {'Alert phones · '}
                {I(phoneLine)}
              </span>
              <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;text-wrap:pretty;')}>
                {"Each number is confirmed by a code text before any alert text or call goes to it. Staff confirm their own the first time they sign in, or you set it; after that only you change it. Every change is logged."}
              </span>
            </span>
            {resendable && !resent && (
              <a
                href="#"
                {...wr(() => void act(async () => { await userAlertPhoneResend(client, { params: { id: resendable.id }, body: {} }); setResent(true); }))}
                style={sx('display:inline-flex;align-items:center;height:26px;padding:0 10px;border-radius:6px;font-size:16px;font-weight:600;text-decoration:none;white-space:nowrap;background:#F7F3EA;color:#22272E;border:1px solid rgba(34,39,46,.2);')}
              >
                {'Resend code to ' + firstInitial(resendable.name)}
              </a>
            )}
            {smsRoles.data?.roles.map((r) => (
              <span key={r.role} style={sx('display:flex;align-items:center;gap:5px;flex-wrap:nowrap;white-space:nowrap;')}>
                <span style={sx('font-size:16px;font-weight:700;color:#3C454F;')}>
                  {I(r.label)}
                  {' by text:'}
                </span>
                {(['Critical', 'Handoffs', 'Signatures'] as const).map((o) => {
                  const locked = o === 'Critical';
                  const on = locked ? true : o === 'Handoffs' ? r.handoffs : r.signatures;
                  return (
                    <a
                      key={o}
                      href="#"
                      aria-pressed={on}
                      {...wr(() => {
                        if (locked) return;
                        void act(async () => {
                          await facilitySmsRolesSave(client, { params: { id: 'me' }, body: { role: r.role, [o === 'Handoffs' ? 'handoffs' : 'signatures']: !on } });
                          smsRoles.reload();
                        });
                      })}
                      style={sx(SEG_OPT(on) + (locked ? 'cursor:default;opacity:.85;' : ''))}
                    >
                      {I(o)}
                    </a>
                  );
                })}
              </span>
            ))}
            {notServed(smsRoles.error) && <span style={sx('font-size:16px;color:#54606C;')}>Which alerts each role gets by text is not served by the back end yet.</span>}
          </div>
          <MedRulesPanel />
        </>
      )}
      {!facEmpty && (
      <div style={sx('grid-column:1/-1;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:10px 18px;box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);border-bottom:1px solid rgba(34,39,46,.14);padding:10px 0;margin-bottom:12px;')}>
        {tiles.map((t, i, a) => (
          <div key={t[0]} style={sx(tileWrap(i, a.length))}>
            <div style={sx('font-size:14px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#54606C;margin-bottom:6px;')}>{I(t[0])}</div>
            <div style={sx(fig(t[3]))}>{I(t[1])}</div>
            <div style={sx('font-size:16px;color:#54606C;margin-top:6px;')}>{I(t[2])}</div>
          </div>
        ))}
      </div>
      )}
      {!fa && deputy.data && (
        <div data-testid="approver-line" style={sx('font-size:16px;color:#54606C;margin:-4px 0 12px;')}>
          {deputy.data.deputy
            ? I('The owner approves, with ' + (deputy.data.deputy.name || deputy.data.deputy.email || 'the named deputy') + ' as deputy approver.')
            : I('Only the owner approves.')}
        </div>
      )}
      <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;margin-bottom:12px;')}>
        <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;')}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2F5C97" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={sx('flex:0 0 15px;')} aria-hidden="true">
            <circle cx="9" cy="8" r="3.4" />
            <path d="M2.8 20a6.2 6.2 0 0 1 12.4 0" />
            <path d="M17 8h4M19 6v4" />
          </svg>
          <span style={sx('font-size:16px;font-weight:700;')}>Access requests</span>
          <span style={sx('flex:1;')} />
          {!fa && (
            <a href="#" {...wr(() => { setOnboarded(false); setWizard(true); })} style={sx(btn(onboarded ? 'soft' : 'ghost'))}>
              {I(onboarded ? 'Invite sent' : 'Onboard facilities')}
            </a>
          )}
        </div>
        <div style={sx('font-size:16px;color:#54606C;margin-top:9px;')}>{I(requestsNote)}</div>
        {!fa && <AccessRequestRows requests={requests.data?.requests ?? []} onChanged={() => { requests.reload(); changed(); }} />}
        {!fa && <PendingPeople onChanged={() => { reloadUsers(); changed(); }} />}
        {!fa && wizard && <OnboardWizard onClose={() => setWizard(false)} onDone={() => { setOnboarded(true); changed(); }} />}
      </div>
      <DeletionRequestsBlock facility={fa} orgName={orgName} onChanged={() => { reloadUsers(); changed(); }} />
      <ErrLine error={err} style="margin:-4px 0 10px;" />
      {!facEmpty && (
      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,380px),1fr));gap:12px 22px;align-items:start;')}>
        <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>Team members</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{I(users.length + ' users · RBAC')}</span>
          </div>
          <div style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;margin-bottom:12px;flex-wrap:wrap;row-gap:8px;')}>
            {(fa ? ['All', 'Admin', 'Nurse', 'CNA'] : ['All', 'Admin', 'Sales', 'Care', 'Resident']).map((n) => (
              <a key={n} href="#" aria-pressed={filter === n} onClick={(e) => { pre(e); setFilter(n); }} style={sx(seg(filter === n))}>
                {I(n)}
              </a>
            ))}
            <input type="text" aria-label="Search name or email" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name or email" style={sx('flex:1;min-width:150px;height:32px;padding:0 12px;border:1px solid rgba(34,39,46,.14);border-radius:8px;background:#F7F3EA;font-size:16px;color:#22272E;font-family:inherit;box-sizing:border-box;')} />
            <span style={sx('font-size:16px;color:#54606C;flex:0 1 auto;min-width:0;')}>{I(uRows.length + ' / ' + users.length)}</span>
            <a href="#" {...wr(() => setInvite({ name: '', email: '', role: fa ? 'CAREGIVER' : 'OPERATOR', ...(fa ? { title: 'CNA' as const } : {}) }))} style={sx(btn(invited ? 'soft' : 'primary'))}>
              {I(invited ? '+ Invite another' : '+ Invite user')}
            </a>
          </div>
          {invite && (
            <div data-testid="settings-invite" style={sx('display:flex;align-items:center;gap:8px;flex-wrap:wrap;row-gap:7px;margin-bottom:12px;')}>
              {fa && (
                <div role="radiogroup" aria-label="Invite as" style={sx('display:flex;gap:6px;flex-basis:100%;')}>
                  {([['Nurse', 'CAREGIVER', 'Nurse'], ['CNA', 'CAREGIVER', 'CNA'], ['Family member', 'FAMILY_MEMBER', undefined], ['Resident', 'RESIDENT', undefined]] as const).map(([label, role, title]) => {
                    const on = invite.role === role && invite.title === title;
                    return (
                      <a key={label} href="#" role="radio" aria-checked={on} onClick={(e) => { pre(e); setInvite({ ...invite, role, title }); }} style={sx(seg(on))}>
                        {I(label)}
                      </a>
                    );
                  })}
                </div>
              )}
              {fa && invite.role === 'RESIDENT' && (
                <select
                  aria-label="Which resident"
                  value={invite.residentId ?? ''}
                  onChange={(e) => {
                    const r = (roster.data?.residents ?? []).find((x) => x.id === e.target.value);
                    setInvite({ ...invite, residentId: e.target.value || undefined, name: r?.name ?? invite.name });
                  }}
                  style={sx('flex:1 1 160px;height:30px;padding:0 8px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;')}
                >
                  <option value="">Which resident</option>
                  {(roster.data?.residents ?? []).map((r) => (
                    <option key={r.id} value={r.id}>{r.name ?? 'Resident'}</option>
                  ))}
                </select>
              )}
              <input type="text" aria-label="Invite name" placeholder="Name" value={invite.name} onChange={(e) => setInvite({ ...invite, name: e.target.value })} style={sx('flex:1 1 140px;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;')} />
              <input type="email" aria-label="Invite email" placeholder="Email" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} style={sx('flex:1 1 160px;height:30px;padding:0 10px;border:1px solid rgba(34,39,46,.2);border-radius:7px;background:#FBF8F1;font-size:16px;font-family:inherit;color:#22272E;box-sizing:border-box;')} />
              <a
                href="#"
                {...wr(() => void act(async () => {
                  const r = await adminInviteCreate(client, {
                    body: {
                      email: invite.email.trim(), name: invite.name.trim() || undefined, role: invite.role,
                      ...(invite.title ? { careTitle: invite.title } : {}),
                      ...(invite.role === 'RESIDENT' && invite.residentId ? { residentId: invite.residentId } : {}),
                      ...(fa && facilityId ? { organizationId: facilityId } : {}),
                    },
                  });
                  setLinks((l) => [{ who: invite.name.trim() || invite.email.trim(), url: r.inviteUrl, pending: !!r.pendingApproval }, ...l]);
                  setInvite(null);
                  setInvited(true);
                  reloadUsers();
                  if (seesInvites) invites.reload();
                }))}
                style={sx(btn('primary'))}
              >
                Create invite link
              </a>
              <a href="#" onClick={(e) => { pre(e); setInvite(null); }} style={sx(btn('ghost'))}>Cancel</a>
            </div>
          )}
          {links.map((l) => (
            <div key={l.url} style={sx('margin-bottom:8px;')}>
              {l.pending && <div style={sx('font-size:16px;color:#54606C;')}>{I('Waits for the owner’s approval; the link works after that.')}</div>}
              <InviteLink url={l.url} who={l.who} />
            </div>
          ))}
          {pendingInvites.length > 0 && (
            <div data-testid="settings-pending-invites" style={sx('margin-bottom:10px;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin:2px 0 6px;')}>{I('Pending invites · ' + pendingInvites.length)}</div>
              {pendingInvites.map(({ i, st }) => (
                <div key={i.id} data-testid={'invite-row-' + i.id} style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:8px 11px;border-radius:9px;margin-bottom:5px;background:rgba(34,39,46,.03);')}>
                  <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                    <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(i.name || i.email)}</span>
                    <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(i.email)}</span>
                  </span>
                  <span style={sx(chip(toneOf(i.role)[0], toneOf(i.role)[1]))}>{I(CHIP[i.role] ?? i.role)}</span>
                  <span style={sx('font-size:16px;font-weight:600;color:' + (st.tone === 'dead' ? '#B3261E' : st.tone === 'wait' ? '#8A5A00' : '#54606C') + ';')}>{I(st.text)}</span>
                </div>
              ))}
            </div>
          )}
          {uRows.map((u) => {
            const t = toneOf(u.role);
            return (
              <a
                key={u.id}
                href="#"
                aria-current={sel?.id === u.id ? 'true' : undefined}
                onClick={(e) => { pre(e); setSelId(u.id); }}
                style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;padding:10px 11px;border-radius:9px;text-decoration:none;color:inherit;margin-bottom:5px;' + (sel?.id === u.id ? 'background:rgba(79,138,214,.09);box-shadow:inset 3px 0 0 #2F5C97;' : ''))}
              >
                <span style={sx('display:flex;align-items:center;justify-content:center;width:30px;height:30px;flex:0 0 30px;border-radius:8px;font-size:16px;font-weight:700;color:' + t[0] + ';background:' + t[1] + ';')}>{I(initials(u.name))}</span>
                <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;color:#22272E;')}>{I(u.name)}</span>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;')}>{I(u.handle + ' · ' + u.email)}</span>
                </span>
                <span style={sx(chip(t[0], t[1]))}>{I(u.chipRole)}</span>
                {fa && <CredentialChip c={u.credential} />}
                {needsPhone(u) && (
                  <span data-testid={'needs-phone-' + u.id} title={u.alertPhone ? 'A number is on file but not confirmed by its code yet, so no alert text or call goes to it.' : 'No phone number, so no alert text or call can reach them.'} style={sx(chip('#8A5A00', 'rgba(138,90,0,.12)'))}>
                    {I(u.alertPhone ? 'PHONE NOT CONFIRMED' : 'NEEDS A PHONE NUMBER')}
                  </span>
                )}
                <span style={sx(stateStyle(u.status))}>
                  <span style={sx(stateDot(u.status))} />
                  {I(u.status)}
                </span>
              </a>
            );
          })}
          {uRows.length === 0 && (
            <div style={sx('font-size:16px;color:#54606C;padding:26px 0;text-align:center;')}>{I(uQuery ? 'Nobody matches "' + query.trim() + '".' : 'No users with that role.')}</div>
          )}
        </div>

        <div data-testid="settings-user-detail" style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;min-width:0;')}>
          <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
            <span style={sx('width:7px;height:7px;border-radius:50%;background:#2F5C97;')} />
            <span style={sx('font-size:16px;font-weight:700;')}>User detail</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-size:16px;color:#54606C;')}>{I(sel?.name ?? '')}</span>
          </div>
          {sel && (
            <>
              <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;margin-bottom:14px;')}>
                <span style={sx('display:flex;align-items:center;justify-content:center;width:36px;height:36px;flex:0 0 36px;border-radius:10px;font-size:16px;font-weight:700;color:' + toneOf(sel.role)[0] + ';background:' + toneOf(sel.role)[1] + ';')}>{I(initials(sel.name))}</span>
                <span style={sx('min-width:min(100%,130px);flex:1 1 130px;')}>
                  <span style={sx('display:block;font-size:16px;font-weight:700;')}>{I(sel.name)}</span>
                  <span style={sx('display:block;font-size:16px;color:#54606C;margin-top:2px;')}>{I(sel.handle + ' · ' + sel.email)}</span>
                </span>
                <span style={sx(stateStyle(sel.status))}>
                  <span style={sx(stateDot(sel.status))} />
                  {I(sel.status)}
                </span>
              </div>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:8px;')}>Role</div>
              <div role="radiogroup" aria-label="Role" style={sx('display:flex;flex-wrap:wrap;row-gap:6px;gap:5px;margin-bottom:14px;')}>
                {ROLES.map((r) => (
                  <a
                    key={r}
                    href="#"
                    role="radio"
                    aria-checked={selPill === r}
                    aria-disabled={lockR || !canWrite || undefined}
                    title={roReason ?? (lockR ? 'Only a Super Admin changes roles.' : undefined)}
                    onClick={(e) => { pre(e); setRole(r); }}
                    style={sx(seg(selPill === r) + (lockR ? 'opacity:.5;cursor:not-allowed;' : ''))}
                  >
                    {I(r)}
                  </a>
                ))}
              </div>
              {fa && ['CAREGIVER', 'STAFF', 'ADMIN'].includes(sel.role) && <AlertPhoneEditor key={sel.id} userId={sel.id} name={sel.name} />}
              {fa && ['CAREGIVER', 'STAFF', 'ADMIN'].includes(sel.role) && <CredentialEditor key={'cred' + sel.id} userId={sel.id} name={sel.name} credential={sel.credential ?? null} onSaved={() => { reloadUsers(); changed(); }} />}
              {fa && ['CAREGIVER', 'STAFF', 'ADMIN'].includes(sel.role) && <NpiEditor key={'npi' + sel.id} userId={sel.id} name={sel.name} npi={sel.npi ?? null} onSaved={() => { reloadUsers(); changed(); }} />}
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:8px;')}>{I('Permissions · ' + (selPill ?? sel.role).toLowerCase())}</div>
              <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(min(100%,220px),1fr));column-gap:16px;')}>
                {PERMS.map((p, i) => ({ p, i })).filter(({ i }) => PERM_SHOW.includes(i)).map(({ p, i }) => (
                  <div key={p} style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;padding:7px 0;border-top:1px solid rgba(34,39,46,.07);')}>
                    <span style={sx('display:inline-flex;align-items:center;justify-content:center;width:17px;height:17px;flex:0 0 17px;border-radius:5px;font-size:16px;font-weight:700;white-space:nowrap;' + (grant[i] ? 'background:rgba(47,138,111,.18);color:#12503C;' : 'background:rgba(179,38,30,.12);color:#B3261E;'))}>
                      {I(grant[i] ? '✓' : '✕')}
                    </span>
                    <span style={sx('font-size:16px;color:' + (grant[i] ? '#22272E' : '#54606C') + ';min-width:min(100%,130px);flex:1 1 130px;')}>{I(p)}</span>
                  </div>
                ))}
              </div>
              {(
                <>
                  <div style={sx('margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
                    <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#54606C;font-weight:600;margin-bottom:9px;')}>Multi-sig eligibility</div>
                    <div style={sx('display:flex;align-items:center;gap:11px;flex-wrap:wrap;row-gap:6px;')}>
                      <span style={sx('font-size:16px;color:#3C454F;min-width:min(100%,130px);flex:1 1 130px;')}>
                        {'Eligible approver for '}
                        <b style={sx('font-weight:700;')}>held actions</b>
                      </span>
                      <a
                        href="#"
                        role="switch"
                        aria-checked={sel.multiSig}
                        aria-label="Eligible approver for held actions"
                        {...wr(() => void act(async () => { await adminUserUpdate(client, { params: { id: sel.id }, body: { multiSig: !sel.multiSig } }); reloadUsers(); }))}
                        style={sx('display:inline-flex;align-items:center;width:42px;height:24px;flex:0 0 42px;border-radius:14px;padding:2px;box-sizing:border-box;text-decoration:none;white-space:nowrap;' + (sel.multiSig ? 'background:#1F6B52;justify-content:flex-end;' : 'background:rgba(34,39,46,.22);justify-content:flex-start;'))}
                      >
                        <span style={sx('display:block;width:20px;height:20px;border-radius:50%;background:#fff;box-shadow:0 1px 3px rgba(34,39,46,.28);')} />
                      </a>
                    </div>
                    <div style={sx('font-size:16px;color:#54606C;margin-top:8px;line-height:1.5;')}>
                      {I(sel.multiSig
                        ? sel.name + ' can now countersign a held action. Two distinct people are still required, so they cannot approve their own.'
                        : 'Off. A held action needs two other eligible approvers before it can run.')}
                    </div>
                  </div>
                  <div style={sx('display:flex;align-items:center;gap:10px;flex-wrap:wrap;row-gap:6px;margin-top:14px;padding-top:12px;border-top:1px solid rgba(34,39,46,.1);')}>
                    <span style={sx('font-size:16px;color:#54606C;min-width:min(100%,130px);flex:1 1 130px;')}>
                      {I(sessions.data ? 'Active sessions: ' + nSessions + '.' + (nSessions ? '' : ' No active sessions.') : sessions.error ? 'Sessions could not be loaded.' : 'Active sessions: –')}
                    </span>
                    <a href="#" {...wr(() => void act(async () => {
                      const r = await adminUserResetLink(client, { params: { id: sel.id }, body: {} });
                      setResetSent(sel.id);
                      setResetNote(r.sent ? null : r.reason ?? 'The link was not sent.');
                    }))} style={sx(btn(resetSent === sel.id ? 'soft' : 'ghost'))}>
                      {I(resetSent === sel.id ? (resetNote ? 'Reset link not sent' : 'Reset link sent') : 'Reset password')}
                    </a>
                    {sel.orgId && ['CAREGIVER', 'STAFF', 'ADMIN'].includes(sel.role) && (
                      <CustomRolePicker userId={sel.id} orgId={sel.orgId} current={sel.customRoleId ?? null} onChanged={() => { reloadUsers(); changed(); }} />
                    )}
                    <UserAccountActions user={sel} sessions={nSessions} onChanged={() => { reloadUsers(); sessions.reload(); changed(); }} />
                    {resetSent === sel.id && resetNote && <span role="status" style={sx('flex-basis:100%;font-size:16px;color:#B3261E;')}>{I(resetNote)}</span>}
                  </div>
                  <AccountBlock key={'acct' + sel.id} user={sel} facility={fa} orgName={orgName} activeAdmins={activeAdmins} viewerOwner={viewerOwner} teamAccount={!fa && TEAM_ROLES.has(sel.role as Role)} onChanged={() => { reloadUsers(); sessions.reload(); changed(); }} />
                </>
              )}
            </>
          )}
        </div>
        <BulkAdd onAdded={reloadUsers} />
        {fa && (facilityId ?? session?.user.organizationId) && <CustomRolesPanel orgId={(facilityId ?? session?.user.organizationId)!} orgName={orgName} />}
      </div>
      )}
      {facTab && <BuildingsList />}
    </div>
  );
}
